import axios from 'axios';
import { expireStoredSession, getToken } from './auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = String(error.config?.url || '');
    const detail = String(error.response?.data?.detail || error.response?.data?.error || '');
    const isEncyePublishFailure = requestUrl.includes('publish-to-encye')
      || requestUrl.includes('check-existing-encye')
      || detail.toLowerCase().includes('encye session');

    if (error.response?.status === 401 && !isEncyePublishFailure) {
      expireStoredSession();
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: (data) => api.post('/auth/login', data),
  google: (data) => api.post('/auth/google', data),
  selectOrganization: (data) => api.post('/auth/select-organization', data),
  switchOrganization: (data) => api.post('/auth/switch-organization', data),
  me: () => api.get('/auth/me'),
};

export const getApiErrorMessage = (error, fallback = 'Something went wrong.') => {
  const status = error?.response?.status;
  const message = error?.response?.data?.detail || error?.response?.data?.error || error?.response?.data?.message || error?.message;
  if (status === 401) return 'Please login again';
  if (status === 403) return message || 'You do not have permission to perform this action.';
  if (!error?.response) return 'Unable to reach the backend. Please check if the backend is running.';
  return message || fallback;
};

export const parseFetchErrorMessage = async (response, fallback = 'Request failed.') => {
  const text = await response.text();
  try {
    const data = JSON.parse(text);
    return data.detail || data.error || data.message || fallback;
  } catch {
    return text || fallback;
  }
};

export const ragService = {
  createSession: (ragOrgName) => api.post('/rag/session', { ragOrgName }),
};

export const smeService = {
  getAll: () => api.get('/smes'),
  getById: (id) => api.get(`/smes/${id}`),
  create: (data) => api.post('/smes', data),
  import: (data) => api.post('/smes/import', data),
  update: (id, data) => api.put(`/smes/${id}`, data),
  updatePrompt: (id, data) => api.put(`/smes/${id}/prompt`, data),
  revertPrompt: (id) => api.post(`/smes/${id}/prompt/revert`),
  updateFavorite: (id, isFavorite) => api.patch(`/smes/${id}/favorite`, { isFavorite }),
  markRecent: (id) => api.post(`/smes/${id}/recent`),
  toggleStatus: (id, isActive) => api.patch(`/smes/${id}/status`, { isActive }),
  delete: (id) => api.delete(`/smes/${id}`),
};

export const editorService = {
  getAll: () => api.get('/editors'),
  getById: (id) => api.get(`/editors/${id}`),
  create: (data) => api.post('/editors', data),
  update: (id, data) => api.put(`/editors/${id}`, data),
  updatePrompt: (id, data) => api.put(`/editors/${id}/prompt`, data),
  revertPrompt: (id) => api.post(`/editors/${id}/prompt/revert`),
  toggleStatus: (id, isActive) => api.patch(`/editors/${id}/status`, { isActive }),
  delete: (id) => api.delete(`/editors/${id}`),
};

export const articleService = {
  generate: (data) => api.post('/articles/generate', data),
  publishToEncye: (data) => api.post('/articles/publish-to-encye', data),
  getBaseSystemPrompt: (params) => api.get('/articles/base-system-prompt', { params }),
  updateBaseSystemPrompt: (data) => api.put('/articles/base-system-prompt', data),
  revertBaseSystemPrompt: () => api.post('/articles/base-system-prompt/revert'),
};

export const lexiconService = {
  getAll: (params) => api.get('/lexicons', { params }),
  getById: (id) => api.get(`/lexicons/${id}`),
  create: (data) => api.post('/lexicons', data),
  update: (id, data) => api.put(`/lexicons/${id}`, data),
  delete: (id) => api.delete(`/lexicons/${id}`),
};

export const lexiconAgentService = {
  getOptions: () => api.get('/lexicon/agent/options'),
  getStyles: () => api.get('/lexicon/agent/styles'),
  createStyle: (data) => api.post('/lexicon/agent/styles', data),
  updateStyle: (value, data) => api.patch(`/lexicon/agent/styles/${encodeURIComponent(value)}`, data),
  deleteStyle: (value) => api.delete(`/lexicon/agent/styles/${encodeURIComponent(value)}`),
  updateSettings: (data) => api.patch('/lexicon/agent/settings', data),
  summarize: (data, config) => api.post('/lexicon/summarize/openai', data, config),
  discoverVariants: (data) => api.post('/lexicon/variants/discover', data),
  discoverVariantsBatch: (data) => api.post('/lexicon/variants/discover-batch', data),
  createGenerationJob: (data) => api.post('/lexicon/generation-jobs', data),
  getGenerationJob: (id) => api.get(`/lexicon/generation-jobs/${id}`),
  getGenerationJobItems: (id, params) => api.get(`/lexicon/generation-jobs/${id}/items`, { params }),
  cancelGenerationJob: (id) => api.post(`/lexicon/generation-jobs/${id}/cancel`),
  retryFailedGenerationJob: (id) => api.post(`/lexicon/generation-jobs/${id}/retry-failed`),
  listArticles: (params) => api.get('/lexicon/articles', { params }),
  checkExistingArticles: (data) => api.post('/lexicon/articles/check-existing', data),
  checkExistingEncyeLexicons: (data) => api.post('/lexicon/articles/check-existing-encye', data),
  getArticle: (id) => api.get(`/lexicon/articles/${id}`),
  saveArticle: (data) => api.post('/lexicon/articles', data),
  publishArticleToEncye: (id, data = {}) => api.post(`/lexicon/articles/${id}/publish-to-encye`, data),
  getArticleVariants: (id) => api.get(`/lexicon/articles/${id}/variants`),
  linkArticleVariants: (id, data) => api.post(`/lexicon/articles/${id}/variants`, data),
  updateArticle: (id, data) => api.patch(`/lexicon/articles/${id}`, data),
  deleteArticle: (id) => api.delete(`/lexicon/articles/${id}`),
};

export const adminService = {
  overview: () => api.get('/admin/overview'),
  users: (params) => api.get('/admin/users', { params }),
  lexicons: (params) => api.get('/admin/lexicons', { params }),
  generationJobs: (params) => api.get('/admin/generation-jobs', { params }),
  restartFailedGenerationJob: (id) => api.post(`/admin/generation-jobs/${id}/restart-failed`),
  limits: () => api.get('/admin/limits'),
  updateLimits: (data) => api.patch('/admin/limits', data),
  updateTokenWindow: (data) => api.patch('/admin/token-window', data),
};

export default api;
